import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { uiReducer } from '../../store/reducers/ui.reducer';
import { getShowTutorials } from '../../store/selectors/ui.selector';

// --- Video Player component using video.js ---
interface VideoPlayerProps {
  src: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReturnType<typeof videojs> | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    const videoElement = document.createElement('video-js');
    videoElement.classList.add('vjs-big-play-centered', 'vjs-fluid');
    videoRef.current.appendChild(videoElement);

    const player = videojs(videoElement, {
      controls: true,
      responsive: true,
      fluid: true,
      preload: 'metadata',
      sources: [{ src, type: 'video/mp4' }],
    });

    playerRef.current = player;

    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [src]);

  return <div ref={videoRef} data-vjs-player className="w-full aspect-video rounded-lg overflow-hidden" />;
};

// --- Tutorial data ---
interface Tutorial {
  i18nKey: string;
  videoUrl: string;
}

const tutorials: Tutorial[] = [
  {
    i18nKey: 'examples',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/01_examples_try_it.mp4',
  },
  {
    i18nKey: '3dFlow',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/02_new_project_3d_flow.mp4',
  },
  {
    i18nKey: 'polygonFlow',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/03_new_project_2d_flow.mp4',
  },
  {
    i18nKey: 'catalogs',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/04_catalogues.mp4',
  },
  {
    i18nKey: 'autoPosition',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/05_chl_lmn_autoposition.mp4',
  },
  {
    i18nKey: 'manualLuminaires',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/06_manual_adding_lum.mp4',
  },
  {
    i18nKey: 'optimization',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/07_optimization.mp4',
  },
  {
    i18nKey: 'marketMatch',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/08_market_match.mp4',
  },
  {
    i18nKey: 'export',
    videoUrl: 'https://storage.googleapis.com/taman-public/assets/onboarding-videos/09_export_report.mp4',
  },
];

// Card width: fits 2.5 cards so the last visible one is always cut in half
const CARD_GAP = 40;
const CONTAINER_PADDING = 96; // px-12 * 2 = 96px
const getCardWidth = () => {
  const containerWidth = window.innerWidth * 0.8 - CONTAINER_PADDING;
  const cardWidth = (containerWidth - 2 * CARD_GAP) / 2.5;
  return Math.max(cardWidth, 280);
};

export const TutorialsModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(getShowTutorials);
  const { setShowTutorials } = uiReducer.actions;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(400);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const handleClose = useCallback(() => {
    dispatch(setShowTutorials(false));
  }, [dispatch, setShowTutorials]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => setCardWidth(getCardWidth());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !isOpen) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const step = cardWidth + CARD_GAP;
      const index = Math.round(scrollLeft / step);
      // Cap at tutorials.length - 3 because the last 2 cards share the last dot (we always show 2.5 cards)
      setActiveIndex(Math.min(index, Math.max(0, tutorials.length - 3)));

      // Check if scrolled to the start/end (within 10px tolerance)
      setShowLeftFade(scrollLeft > 10);
      const isAtEnd = scrollLeft + container.clientWidth >= container.scrollWidth - 10;
      setShowRightFade(!isAtEnd);
    };

    // Initial check
    setShowLeftFade(container.scrollLeft > 10);
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
    setShowRightFade(!isAtEnd);

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen, cardWidth]);

  if (!isOpen) return null;

  const scrollToCard = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const step = cardWidth + CARD_GAP;
    container.scrollTo({
      left: index * step,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
    >
      <style>{`
        .tutorials-scroll::-webkit-scrollbar { display: none; }
        .tutorial-card-text::-webkit-scrollbar { width: 3px; }
        .tutorial-card-text::-webkit-scrollbar-track { background: transparent; }
        .tutorial-card-text::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
        .tutorial-card-text::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
        .tutorials-modal .video-js { border-radius: 8px; overflow: hidden; background: #000; }
        .tutorials-modal .vjs-big-play-button { border: none !important; background: rgba(255,255,255,0.15) !important; }
        .tutorials-modal .video-js .vjs-control-bar { background: rgba(50,50,50,0.9) !important; }
        .tutorials-modal .video-js .vjs-play-progress,
        .tutorials-modal .video-js .vjs-volume-level { background: #999 !important; }
        .tutorials-modal .video-js .vjs-slider:focus .vjs-play-progress { background: #bbb !important; }
        .tutorials-modal .video-js .vjs-load-progress { background: rgba(255,255,255,0.1) !important; }
        .tutorials-modal .video-js .vjs-load-progress div { background: rgba(255,255,255,0.15) !important; }
      `}</style>

      {/* Modal container */}
      <div
        className="tutorials-modal relative w-[80vw] h-[80vh] min-[2000px]:h-[65vh] bg-[#1c1c1c] rounded-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-12 pt-6 pb-4 shrink-0 border-b border-white/10">
          <h1 className="text-xl font-semibold text-white">{t('onboarding.title')}</h1>
          <button
            onClick={handleClose}
            className="flex items-center gap-1.5 px-3 py-2 border border-[#747474] rounded text-white text-xs font-medium hover:bg-white/10 transition-colors cursor-pointer"
          >
            <span>{t('onboarding.close')}</span>
            <X size={14} />
          </button>
        </div>

        {/* Scrollable cards container */}
        <div className="relative flex-1 overflow-hidden">
          <div
            ref={scrollRef}
            className="tutorials-scroll h-full flex px-12 py-6 overflow-x-auto overflow-y-hidden"
            style={{
              scrollSnapType: 'x mandatory',
              scrollPaddingLeft: '48px',
              scrollbarWidth: 'none',
            }}
          >
            {tutorials.map((tutorial, i) => {
              const stepNumber = String(i + 1).padStart(2, '0');
              const isLast = i === tutorials.length - 1;
              const title = t(`onboarding.tutorials.${tutorial.i18nKey}.title`);
              const description = t(`onboarding.tutorials.${tutorial.i18nKey}.description`);

              return (
                <React.Fragment key={i}>
                  {/* Card */}
                  <div
                    className="shrink-0 flex flex-col gap-3 overflow-hidden"
                    style={{
                      width: cardWidth,
                      scrollSnapAlign: 'start',
                    }}
                  >
                    {/* Video */}
                    <VideoPlayer src={tutorial.videoUrl} />

                    {/* Title */}
                    <h2 className="text-base font-semibold text-white leading-snug mt-1 pr-2">
                      <span className="text-[#B3EC49]">{stepNumber}.</span> {title}
                    </h2>

                    {/* Description text */}
                    <div
                      className="tutorial-card-text overflow-y-auto flex-1 pr-2"
                      style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}
                    >
                      <div className="text-[13px] text-gray-400 leading-relaxed">
                        {description.split('\n').map((line, li) => {
                          const trimmed = line.trimStart();
                          if (trimmed.startsWith('- ')) {
                            return (
                              <div key={li} className="flex items-start gap-2 mt-1">
                                <span className="mt-[7px] w-[5px] h-[5px] rounded-full bg-[#B3EC49] shrink-0" />
                                <span>{trimmed.slice(2)}</span>
                              </div>
                            );
                          }
                          if (trimmed === '') {
                            return <div key={li} className="h-2" />;
                          }
                          return <div key={li}>{line}</div>;
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Vertical divider between cards */}
                  {!isLast && (
                    <div className="shrink-0 flex items-stretch mx-5">
                      <div className="w-px bg-white/10" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* End spacer */}
            <div className="shrink-0 w-1" />
          </div>

          {/* Left fade overlay */}
          <div
            className="pointer-events-none absolute top-0 left-0 h-full w-32 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(to left, transparent, #1c1c1c)',
              opacity: showLeftFade ? 1 : 0,
            }}
          />

          {/* Right fade overlay */}
          <div
            className="pointer-events-none absolute top-0 right-0 h-full w-32 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(to right, transparent, #1c1c1c)',
              opacity: showRightFade ? 1 : 0,
            }}
          />

          {/* Navigation arrow — previous */}
          <button
            onClick={() => scrollRef.current?.scrollBy({ left: -(cardWidth + CARD_GAP), behavior: 'smooth' })}
            disabled={!showLeftFade}
            aria-label="Previous"
            className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border transition-colors ${
              !showLeftFade
                ? 'border-white/10 text-white/20 cursor-not-allowed'
                : 'border-white/30 text-white bg-black/40 hover:bg-black/70 cursor-pointer'
            }`}
          >
            <ChevronLeft size={20} />
          </button>

          {/* Navigation arrow — next */}
          <button
            onClick={() => scrollRef.current?.scrollBy({ left: cardWidth + CARD_GAP, behavior: 'smooth' })}
            disabled={!showRightFade}
            aria-label="Next"
            className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border transition-colors ${
              !showRightFade
                ? 'border-white/10 text-white/20 cursor-not-allowed'
                : 'border-white/30 text-white bg-black/40 hover:bg-black/70 cursor-pointer'
            }`}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Pagination dots — we always show ~2.5 cards, so the last 2 are never "first visible" */}
        <div className="flex items-center justify-center gap-1.5 pb-4 shrink-0">
          {tutorials.slice(0, Math.max(1, tutorials.length - 2)).map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToCard(i)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                i === Math.min(activeIndex, tutorials.length - 3)
                  ? 'w-3 h-[5px] bg-[#B3EC49]'
                  : 'w-[5px] h-[5px] bg-[#D9D9D9] opacity-50 hover:opacity-80'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
